import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Carregando } from '../../componentes/estados';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { numero } from '../../nucleo/formato';
import { rotuloModulo } from '../../nucleo/rotulos';

interface Impedimentos {
  usos: { modulo: string; quantidade: number; descricao: string }[];
  titulos_futuros: number;
  pode_cancelar: boolean;
}

/** crm5 (E1): os textos da ação no CRM ("Inativar empresa") no lugar dos do arquivamento do cadastro. */
export interface TextosArquivar {
  pergunta: string;
  texto: string;
  feito: string;
  emUsoTitulo: string;
  emUsoTexto: string;
}

/**
 * Fase 2 (W5, L21, decisão D19): arquivar parceiro em uso. Antes de pedir, mostra o que impede (por módulo, no idioma) e,
 * quando há títulos futuros pendentes no Contas e a pessoa pode cancelar lá, oferece "cancelar e arquivar" com confirmação
 * (nunca a cascata silenciosa do sistema antigo). Sem nada em uso, a confirmação de sempre.
 */
/**
 * crm5 (E1): `textos` troca os rótulos (o CRM "inativa"), e `detalhes` lista, abaixo de cada módulo, o que a tela já conhece e
 * a pessoa pode ver (os títulos das negociações abertas da empresa): a lista do servidor só traz módulo e quantidade.
 */
export function ArquivarParceiro({
  id,
  aberto,
  aoFechar,
  aoArquivado,
  textos,
  detalhes,
}: {
  id: string;
  aberto: boolean;
  aoFechar: () => void;
  aoArquivado: () => void;
  textos?: TextosArquivar;
  detalhes?: Record<string, string[]>;
}) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const q = useQuery({ queryKey: ['cadastros', 'impedimentos', id], queryFn: () => get<Impedimentos>(`/cadastros/parceiros/${id}/impedimentos`), enabled: aberto, staleTime: 0, gcTime: 0 });
  const arquivar = useMutation({
    mutationFn: (cancelar: boolean) => post(`/cadastros/parceiros/${id}/arquivar`, cancelar ? { cancelar_titulos_futuros: true } : {}),
    onSuccess: () => {
      toast.sucesso(textos?.feito ?? t('arquivar.arquivado'));
      setConfirmarCancelar(false);
      aoArquivado();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!aberto) return null;
  if (q.isPending)
    return (
      <Modal aberto aoFechar={aoFechar} titulo={textos?.pergunta ?? t('confirmar.arquivar')}>
        <Carregando />
      </Modal>
    );
  const d = q.data;
  if (!d || d.usos.length === 0)
    return <Confirmar aberto aoFechar={aoFechar} aoConfirmar={() => arquivar.mutate(false)} titulo={textos?.pergunta ?? t('confirmar.arquivar')} texto={textos?.texto ?? t('confirmar.arquivarTexto')} perigoso carregando={arquivar.isPending} />;
  return (
    <>
      <Modal
        aberto={!confirmarCancelar}
        aoFechar={aoFechar}
        titulo={textos?.emUsoTitulo ?? t('arquivar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {d.pode_cancelar && (
              <Botao variante="perigo" onClick={() => setConfirmarCancelar(true)}>
                {t('acoes.cancelarEArquivar', { count: d.titulos_futuros })}
              </Botao>
            )}
          </>
        }
      >
        <p className="text-sm text-tinta-2">{textos?.emUsoTexto ?? t('arquivar.texto')}</p>
        <ul data-impedimentos="" className="mt-3 divide-y divide-borda rounded-lg border border-borda bg-sup-2 text-sm">
          {d.usos.map((u, i) => (
            <li key={`${u.modulo}-${i}`} className="flex flex-col gap-1 px-3 py-2">
              <span className="flex justify-between gap-3">
                <span>{rotuloModulo(u.modulo)}</span>
                <span className="numero shrink-0 text-tinta-2">{numero(u.quantidade)}</span>
              </span>
              {!!detalhes?.[u.modulo]?.length && d.usos.findIndex((x) => x.modulo === u.modulo) === i && (
                <ul data-impedimento-detalhe={u.modulo} className="list-disc pl-5 text-tinta-2">
                  {detalhes[u.modulo]!.map((d, j) => (
                    <li key={`${d}-${j}`} className="break-words">
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {d.titulos_futuros > 0 && <p className="mt-3 text-sm text-tinta-2">{t('arquivar.titulos', { count: d.titulos_futuros })}</p>}
        {d.titulos_futuros > 0 && !d.pode_cancelar && <p className="mt-2 text-xs text-tinta-3">{t('arquivar.semPermissao')}</p>}
      </Modal>
      <Confirmar
        aberto={confirmarCancelar}
        aoFechar={() => setConfirmarCancelar(false)}
        aoConfirmar={() => arquivar.mutate(true)}
        titulo={t('arquivar.confirmarTitulo')}
        texto={t('arquivar.confirmarTexto', { count: d.titulos_futuros })}
        perigoso
        carregando={arquivar.isPending}
      />
    </>
  );
}
